import type { ClinicianWorkbenchPatientRecordV1 } from "@amplecare/contracts";

import {
  formatCaseTime,
  formatDateWithMonthName,
  formatProvenanceTimestamp,
  formatSexText,
} from "../record/format.js";
import { computeAgeYears } from "../record/scoring.js";

export function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

/** Populated value, or a blank fill-in line matching the paper forms. */
export function fieldValue(value: string | null | undefined, blankWidth = 90): string {
  if (value == null || value.trim().length === 0) {
    return `<span class="blank" style="min-width:${String(blankWidth)}px"></span>`;
  }

  return `<span class="value">${escapeHtml(value)}</span>`;
}

export function checkbox(checked: boolean | null, label: string): string {
  const glyph = checked === true ? "&#9746;" : "&#9744;";

  return `<span class="checkbox">${glyph} ${escapeHtml(label)}</span>`;
}

export function yesNoBoxes(value: boolean | null): string {
  return `${checkbox(value === true, "Yes")} ${checkbox(value === false, "No")}`;
}

export function signatureRow(entries: { label: string }[]): string {
  const cells = entries
    .map(
      (entry) =>
        `<div class="sig"><span class="sig-line"></span><span class="sig-label">${escapeHtml(entry.label)}</span></div>`,
    )
    .join("");

  return `<div class="sig-row">${cells}</div>`;
}

export function sectionBar(title: string): string {
  return `<div class="section-bar">${escapeHtml(title)}</div>`;
}

export function patientHeaderBox(
  record: ClinicianWorkbenchPatientRecordV1,
): string {
  const name =
    record.patient.lastName || record.patient.firstName
      ? `${record.patient.lastName ?? ""}${record.patient.lastName && record.patient.firstName ? ", " : ""}${record.patient.firstName ?? ""}`
      : null;
  const age = computeAgeYears(
    record.patient.dateOfBirth,
    record.case.dateOfService,
  );

  return `
    <div class="patient-box">
      <div class="patient-box-name">${name ? escapeHtml(name) : '<span class="blank" style="min-width:120px"></span>'}</div>
      <div class="patient-box-grid">
        <div>MRN: ${fieldValue(record.patient.mrn, 60)}</div>
        <div></div>
        <div>DOB: ${fieldValue(formatDateWithMonthName(record.patient.dateOfBirth), 60)}</div>
        <div>Age: ${fieldValue(age == null ? null : String(age), 30)}</div>
        <div>DOS: ${fieldValue(formatDateWithMonthName(record.case.dateOfService), 60)}</div>
        <div>Sex: ${fieldValue(formatSexText(record.patient.sex), 45)}</div>
        <div class="span-2">Surgeon: ${fieldValue(record.case.surgeonName, 110)}</div>
      </div>
    </div>`;
}

export function docBanner(
  title: string,
  documentIndex: number,
  documentTotal: number,
): string {
  return `
    <div class="doc-banner">
      <span class="doc-banner-title">${escapeHtml(title.toUpperCase())}</span>
      <span class="doc-banner-count">Document ${String(documentIndex)} of ${String(documentTotal)}</span>
    </div>`;
}

export function documentTitleBlock(input: {
  record: ClinicianWorkbenchPatientRecordV1;
  subtitle?: string | null;
  title: string;
}): string {
  const procedures =
    input.record.case.procedures.length > 0
      ? input.record.case.procedures.join(", ")
      : null;
  const caseTime = formatCaseTime(input.record.case.caseTime);

  return `
    <div class="doc-header">
      <div class="doc-header-left">
        <div class="brand">VIP SURGICARE</div>
        <h1>${escapeHtml(input.title)}</h1>
        <div class="planned-procedure">Planned Procedure: ${fieldValue(procedures, 140)}${caseTime ? ` <span class="value-muted">(Time of case: ${escapeHtml(caseTime)})</span>` : ""}</div>
        ${input.subtitle ? `<div class="doc-subtitle">${escapeHtml(input.subtitle)}</div>` : ""}
      </div>
      ${patientHeaderBox(input.record)}
    </div>`;
}

export function provenanceFooter(
  record: ClinicianWorkbenchPatientRecordV1,
): string {
  const metaEntries = Object.values(record.fieldMeta);

  if (metaEntries.length === 0) {
    return `<div class="doc-footer">No captured data on file.</div>`;
  }

  const patientProvided = metaEntries.filter(
    (meta) => meta.source === "questionnaire",
  );
  const earliestProvided = [...patientProvided, ...metaEntries].sort((a, b) =>
    a.updatedAt.localeCompare(b.updatedAt),
  )[0];
  const latest = [...metaEntries].sort((a, b) =>
    b.updatedAt.localeCompare(a.updatedAt),
  )[0];

  const providedText = earliestProvided
    ? `Provided by patient (${formatProvenanceTimestamp(earliestProvided.updatedAt)})`
    : null;
  const updatedText = latest
    ? `Last updated by ${escapeHtml(latest.updatedBy)} (Ample) (${formatProvenanceTimestamp(latest.updatedAt)})`
    : null;

  return `<div class="doc-footer">${[providedText, updatedText]
    .filter((part) => part != null)
    .join(", ")}</div>`;
}

export const chartPackStylesheet = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  @page { size: letter; margin: 0.5in; }
  body { color: #111; font-family: Arial, Helvetica, sans-serif; font-size: 11px; line-height: 1.35; }
  .doc { padding: 12px 4px 20px; page-break-after: always; }
  .doc:last-child { page-break-after: auto; }
  /* On screen, render like a PDF viewer: white letter pages on a gray backdrop. */
  body { background: #8a8f98; padding: 22px 10px; }
  .doc { background: #fff; box-shadow: 0 2px 14px rgba(0, 0, 0, 0.4); margin: 0 auto 26px; max-width: 8.5in; min-height: 11in; padding: 0.55in 0.5in; }
  .doc-banner { align-items: baseline; background: #1f2937; color: #fff; display: flex; justify-content: space-between; margin-bottom: 12px; padding: 5px 10px; }
  .doc-banner-title { font-size: 12.5px; font-weight: 700; letter-spacing: 0.8px; }
  .doc-banner-count { font-size: 9.5px; font-weight: 600; letter-spacing: 0.4px; }
  .doc-header { display: flex; gap: 16px; justify-content: space-between; margin-bottom: 12px; }
  .brand { font-size: 10px; font-weight: 700; letter-spacing: 0.6px; }
  h1 { font-size: 17px; font-weight: 700; line-height: 1.15; }
  .planned-procedure { font-size: 10.5px; margin-top: 3px; }
  .doc-subtitle { color: #333; font-size: 9.5px; margin-top: 2px; }
  .patient-box { border: 1.5px solid #111; flex-shrink: 0; font-size: 10.5px; min-width: 250px; padding: 6px 9px; }
  .patient-box-name { font-size: 12px; font-weight: 700; margin-bottom: 3px; }
  .patient-box-grid { display: grid; gap: 1px 14px; grid-template-columns: auto auto; }
  .patient-box-grid .span-2 { grid-column: span 2; }
  .section-bar { background: #374151; color: #fff; font-size: 11px; font-weight: 700; letter-spacing: 0.6px; margin: 14px 0 6px; padding: 4px 8px; text-transform: uppercase; }
  .value { font-weight: 600; }
  .value-muted { color: #444; font-weight: 400; }
  .blank { border-bottom: 1px solid #444; display: inline-block; height: 11px; }
  .checkbox { margin-right: 12px; white-space: nowrap; }
  table.grid { border-collapse: collapse; width: 100%; }
  table.grid th, table.grid td { border: 1px solid #666; padding: 3px 6px; text-align: left; vertical-align: top; }
  table.grid th { background: #eee; font-size: 10px; }
  .row { border-bottom: 1px solid #bbb; display: flex; flex-wrap: wrap; gap: 4px 18px; padding: 4px 2px; }
  .row-label { font-weight: 600; }
  .two-col { display: grid; gap: 0 24px; grid-template-columns: 1fr 1fr; }
  .kv { border-bottom: 1px solid #bbb; display: grid; gap: 0 10px; grid-template-columns: 150px 1fr; padding: 3.5px 2px; }
  .kv-label { font-weight: 600; }
  .contact-grid { border-bottom: 1px solid #bbb; display: grid; gap: 0 12px; grid-template-columns: 145px 1.2fr 1fr 1fr; padding: 3.5px 2px; }
  .qa-row { align-items: baseline; border-bottom: 1px solid #ccc; display: flex; gap: 10px; padding: 3.5px 2px; }
  .qa-answer { flex-shrink: 0; text-align: right; width: 122px; }
  .ros-row { border-bottom: 1px solid #ccc; display: grid; gap: 0 8px; grid-template-columns: 140px 1fr; padding: 3px 2px; }
  .score-row { border-bottom: 1px solid #ccc; display: flex; gap: 10px; padding: 3.5px 2px; }
  .score-points { flex-shrink: 0; font-weight: 700; min-width: 52px; }
  .note { color: #333; font-size: 9.5px; margin-top: 6px; }
  .sig-row { display: flex; gap: 28px; margin-top: 26px; }
  .sig { display: flex; flex: 1; flex-direction: column; gap: 2px; }
  .sig-line { border-bottom: 1px solid #111; display: block; height: 16px; }
  .sig-label { font-size: 9.5px; }
  .doc-footer { border-top: 1px solid #999; color: #333; font-size: 9px; margin-top: 18px; padding-top: 3px; }
  .consent-text p { margin-bottom: 9px; text-align: justify; }
  .prose-line { padding: 3px 0; }
  @media screen and (max-width: 700px) {
    body { padding: 8px; }
    .doc { min-height: 0; padding: 18px 14px; width: 100%; }
    .doc-header { align-items: stretch; flex-direction: column; gap: 10px; }
    .patient-box { min-width: 0; width: 100%; }
    .patient-box-grid, .two-col { grid-template-columns: 1fr; }
    .patient-box-grid .span-2 { grid-column: span 1; }
    .kv { grid-template-columns: minmax(90px, 36%) 1fr; }
    .contact-grid { grid-template-columns: minmax(90px, 36%) 1fr; }
    .qa-row, .score-row, .sig-row { flex-wrap: wrap; }
    .qa-answer { text-align: left; width: auto; }
    table.grid { display: block; max-width: 100%; overflow-x: auto; }
  }
  @media print {
    body { background: #fff; padding: 0; }
    .doc { box-shadow: none; margin: 0; max-width: none; min-height: 0; padding: 12px 4px 20px; }
  }
`;

export function renderChartPackHtml(input: {
  bodies: string[];
  title: string;
}): string {
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(input.title)}</title>
<style>${chartPackStylesheet}</style>
</head>
<body>
${input.bodies.map((body) => `<div class="doc">${body}</div>`).join("\n")}
</body>
</html>`;
}
